import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtDate, statusClass } from "../lib/utils";
import { Headset, ChatCircleDots, Calendar } from "@phosphor-icons/react";
import { TicketView } from "./ClientSupport";
import PageHeader from "../components/PageHeader";

export default function AdminTickets() {
  const { t, lang } = useI18n();
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/tickets");
    setTickets(data);
  };
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    const { data } = await api.get(`/admin/tickets/${id}`);
    setActive(data);
  };

  if (active) return <TicketView ticket={active} onClose={() => { setActive(null); load(); }} role="admin" />;

  const filtered = filter === "all" ? tickets : tickets.filter((t) => t.status === filter);

  return (
    <div className="space-y-6" data-testid="admin-tickets">
      <PageHeader
        eyebrow="Supporto"
        title="Tutti i ticket"
        subtitle="Ticket dei clienti in tempo reale. Il pallino rosso indica una nuova risposta cliente da vedere."
        chip="tf-chip-sky"
        icon={Headset}
      />

      <div className="tf-card p-4 flex flex-wrap gap-2">
        {["all", "open", "in_progress", "resolved", "closed"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`tf-btn ${filter === s ? "tf-btn-primary" : "tf-btn-ghost"}`} data-testid={`filter-${s}`}>
            {t(s) || s}
          </button>
        ))}
        <span className="ml-auto text-sm text-[var(--tf-muted)] self-center">{filtered.length} / {tickets.length}</span>
      </div>

      <div className="tf-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto scroll-thin">
          <table className="tf-table">
            <thead>
              <tr>
                <th>{t("clients")}</th>
                <th>{t("subject")}</th>
                <th>{t("priority")}</th>
                <th>{t("status")}</th>
                <th>{t("last_update")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} className="text-center text-[var(--tf-muted)] py-10">{t("no_tickets")}</td></tr>}
              {filtered.map((tk) => (
                <tr key={tk.id} className="cursor-pointer" onClick={() => open(tk.id)} data-testid={`admin-ticket-${tk.id}`}>
                  <td>
                    <div className="font-medium">{tk.company_name}</div>
                    <div className="text-xs text-[var(--tf-muted)]">{tk.client_email}</div>
                  </td>
                  <td>
                    {tk.subject}
                    {tk.last_reply_by === "client" && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-[var(--tf-error)]" title="Nuovo messaggio cliente" />}
                  </td>
                  <td><span className={`tf-status-pill ${tk.priority === "high" ? "tf-status-error" : tk.priority === "low" ? "tf-status-neutral" : "tf-status-info"}`}>{t(tk.priority)}</span></td>
                  <td><span className={`tf-status-pill ${statusClass(tk.status === "open" ? "pending" : tk.status === "resolved" ? "active" : "neutral")}`}>{t(tk.status)}</span></td>
                  <td className="text-xs font-mono">{fmtDate(tk.updated_at, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden tf-mlist" data-testid="admin-tickets-mlist">
        {filtered.length === 0 && (
          <div className="tf-card p-8 text-center text-[var(--tf-muted)] text-sm">
            <ChatCircleDots size={28} weight="duotone" className="mx-auto mb-2 opacity-60" />
            {t("no_tickets")}
          </div>
        )}
        {filtered.map((tk) => {
          const priorityColor = tk.priority === "high" ? { bg: "#FBDDE1", fg: "#A11534" }
            : tk.priority === "low" ? { bg: "#F5F2E8", fg: "#193529" }
            : { bg: "#DAECFA", fg: "#1F5A93" };
          return (
            <button
              key={tk.id}
              onClick={() => open(tk.id)}
              className="tf-mrow text-left"
              data-testid={`admin-ticket-card-${tk.id}`}
            >
              <div className="tf-mrow-icon relative" style={{ background: priorityColor.bg, color: priorityColor.fg }}>
                <ChatCircleDots size={20} weight="fill" />
                {tk.last_reply_by === "client" && (
                  <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[var(--tf-error)] border-2 border-white" title="Nuovo messaggio" />
                )}
              </div>
              <div className="tf-mrow-body">
                <div className="tf-mrow-title truncate">{tk.subject}</div>
                <div className="tf-mrow-sub truncate">{tk.company_name}</div>
                <div className="tf-mrow-sub flex items-center gap-1.5 mt-0.5">
                  <Calendar size={11} weight="bold" />
                  {fmtDate(tk.updated_at, lang)}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className={`tf-status-pill ${statusClass(tk.status === "open" ? "pending" : tk.status === "resolved" ? "active" : "neutral")} text-[9px]`}>{t(tk.status)}</span>
                <span className="text-[9px] uppercase tracking-widest text-[var(--tf-muted)] font-bold">{t(tk.priority)}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
